// Wraps the shared `api` helper to add dev-impersonation headers when an engineer
// has an override in localStorage. Scoped to this feature; everything else imports
// the plain `api` and sees the real AAD identity.
//
// Double-gated: the controller reads these headers only under #if DEBUG, and this
// module only stamps them when NODE_ENV is 'development'.

import api from '../../../services/api'
import {
  DEV_OVERRIDE_UPN_KEY,
  DEV_OVERRIDE_ROLES_KEY,
} from './devImpersonation'

const isDev = process.env.NODE_ENV === 'development'

const overrideHeaders = () => {
  if (!isDev) return {}
  const headers = {}
  try {
    const upn = window.localStorage.getItem(DEV_OVERRIDE_UPN_KEY)
    const roles = window.localStorage.getItem(DEV_OVERRIDE_ROLES_KEY)
    if (upn) headers['X-Dev-Override-Upn'] = upn
    if (roles) headers['X-Dev-Override-Roles'] = roles
  } catch (_) {
    // localStorage can throw in private-browsing or constrained iframes.
    // Silent fallback to no-override is the right thing here — the dev
    // panel is non-critical.
  }
  return headers
}

// Patch a fetch response by re-issuing the call with override headers
// when present. We can't tweak headers post-hoc on the shared `api`
// helper without refactoring it, so we just call fetch ourselves here
// for the dev path, falling back to the shared helper when no
// override is set (so the standard MSAL+Content-Type plumbing stays in
// one place for the common case).

import { authProvider } from '../../../services/authProvider'

const baseUrl = '/api'
const apiScope = {
  scopes: ['api://16af2a44-dbad-4ec1-9295-1415aac34763/access_as_user'],
}

const withOverride = async (method, url, data) => {
  const extra = overrideHeaders()
  if (Object.keys(extra).length === 0) {
    // No override active — use the shared api so any future tweaks to
    // the standard helper (interceptors, retry, etc.) apply here too.
    if (method === 'GET') return api.get(url)
    if (method === 'POST') return api.post(url, data)
    if (method === 'PUT') return api.put(url, data)
    if (method === 'DELETE') return api.deleteCall(url)
    throw new Error(`Unsupported method: ${method}`)
  }
  const token = await authProvider.getAccessToken(apiScope)
  const init = {
    method,
    headers: {
      Authorization: 'Bearer ' + token.accessToken,
      'Content-Type': 'application/json',
      ...extra,
    },
  }
  if (data !== undefined && method !== 'GET' && method !== 'DELETE') {
    init.body = JSON.stringify(data)
  }
  return fetch(`${baseUrl}${url}`, init)
}

const get = (url) => withOverride('GET', url)
const post = (url, data) => withOverride('POST', url, data)
const put = (url, data) => withOverride('PUT', url, data)
const deleteCall = (url) => withOverride('DELETE', url)

export default { get, post, put, deleteCall }
